import {
    Box,
    Button,
    CircularProgress,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
    useTheme
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrackedTextField } from 'src/theme/StyledFields';
import ApiClient from '../config/ApiClient';
import { RelEntity } from '../object-actions/types/types';

interface SessionRecording {
    id: number;
    lesson_plan: number;
    session: RelEntity<"LessonPlanSession">;
    slide: RelEntity<"SessionSlide">;
    recording: string;
    duration: number | null;
    file_size: number | null;
    mime_type: string | null;
    notes: string | null;
    recording_type: string;
    started_at: string | null;
    ended_at: string | null;
    created_at?: string;
}

const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const isVideo = (mimeType: string | null | undefined): boolean => {
    return !!mimeType?.startsWith('video/');
};

const isAudio = (mimeType: string | null | undefined): boolean => {
    return !!mimeType?.startsWith('audio/');
};

export default function SessionRecordingsPage() {
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const qLesson = searchParams.get('lesson_plan_id') || '';
    const qSession = searchParams.get('session_id') || '';
    const qSlide = searchParams.get('slide_id') || '';

    const [filterLp, setFilterLp] = useState(qLesson);
    const [filterSession, setFilterSession] = useState(qSession);
    const [filterSlide, setFilterSlide] = useState(qSlide);
    const [loading, setLoading] = useState(true);
    const [recordings, setRecordings] = useState<SessionRecording[]>([]);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setFilterLp(qLesson);
        setFilterSession(qSession);
        setFilterSlide(qSlide);
    }, [qLesson, qSession, qSlide]);

    const fetchRecordings = useCallback(async () => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (qLesson) params.set('lesson_plan', qLesson);
        if (qSession) params.set('session', qSession);
        if (qSlide) params.set('slide', qSlide);

        try {
            const res = await ApiClient.get<{ results?: SessionRecording[] } | SessionRecording[]>(
                `/api/session-recordings/${params.toString() ? `?${params.toString()}` : ''}`
            );
            const list = Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
            setRecordings(list);
            if (!res.success && res.error) setError(res.error);
        } catch (err: any) {
            setError(err?.message || 'Failed to load recordings');
            setRecordings([]);
        } finally {
            setLoading(false);
        }
    }, [qLesson, qSession, qSlide]);

    useEffect(() => {
        fetchRecordings();
    }, [fetchRecordings]);

    const handleApply = () => {
        const next = new URLSearchParams();
        if (filterLp) next.set('lesson_plan_id', filterLp);
        if (filterSession) next.set('session_id', filterSession);
        if (filterSlide) next.set('slide_id', filterSlide);
        setSearchParams(next, { replace: true });
    };

    return (
        <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Session Recordings
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <TrackedTextField
                    label="Lesson plan ID"
                    size="small"
                    data-attr={"lesson-plan-filter"}
                    value={filterLp}
                    onChange={(e) => setFilterLp(e.target.value)}
                    placeholder="e.g. 10"
                    sx={{ minWidth: 140 }}
                />
                <TrackedTextField
                    label="Session ID"
                    size="small"
                    value={filterSession}
                    onChange={(e) => setFilterSession(e.target.value)}
                    placeholder="e.g. 5"
                    sx={{ minWidth: 120 }}
                />
                <TrackedTextField
                    label="Slide ID"
                    size="small"
                    value={filterSlide}
                    onChange={(e) => setFilterSlide(e.target.value)}
                    placeholder="e.g. 12"
                    sx={{ minWidth: 120 }}
                />
                <Button variant="outlined" size="small" onClick={handleApply} disabled={loading}>
                    Apply
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : recordings.length === 0 ? (
                <Typography color="text.secondary">No recordings found. Adjust filters or add recordings from a lesson plan.</Typography>
            ) : (
                <List>
                    {recordings.map((rec, i) => (
                        <ListItem
                            key={rec.id || i}
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                                borderRadius: 1,
                                mb: 1,
                                p: 2
                            }}
                        >
                            <ListItemText
                                primary={`${rec.session != null ? `${rec.session.str}` : ''}${rec.slide != null ? ` • ${rec.slide.str}` : ''}`}

                            />
                            {rec.recording && (
                                <Box sx={{ mt: 2, width: '100%' }}>
                                    {isVideo(rec.mime_type) ? (
                                        <video controls src={rec.recording} style={{ width: '100%', maxWidth: 400, display: 'block', borderRadius: 4 }} />
                                    ) : isAudio(rec.mime_type) ? (
                                        <audio controls src={rec.recording} style={{ width: '100%' }} />
                                    ) : (
                                        <audio controls src={rec.recording} style={{ width: '100%' }} />
                                    )}
                                    <Link href={rec.recording} download={(rec.recording || '').split('/').pop() || 'recording'} underline="hover" variant="body2" sx={{ mt: 1, display: 'inline-block' }}>
                                        Download
                                    </Link>
                                </Box>
                            )}
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}
